// Pure helpers behind the Payslips & Tax Pay tab: de-duplicating payslips, laying them out by month,
// combining several payslips in one month, reconciling deductions and picking a clean chart scale.
const PERIODS_PER_YEAR = { M: 12, W: 52, '2W': 26, '4W': 13 };
const DAY = 24 * 60 * 60 * 1000;
const AMOUNT_FIELDS = ['grossPay', 'salarySacrifice', 'ni', 'tax', 'netPay', 'employerNi', 'employerPension'];
const YTD_FIELDS = ['grossPayYTD', 'salarySacrificeYTD', 'niYTD', 'taxYTD', 'employerNiYTD', 'employerPensionYTD'];
export function monthKey(year, month) {
    return `${year}-${String(month + 1).padStart(2, '0')}`;
}
// Pay periods are counted from April, so a month belongs to the tax year that started in the April before it.
export function taxYearOfMonth(year, month) {
    return month >= 3 ? year : year - 1;
}
function monthName(m, options = { month: 'long' }) {
    return new Date(m.year, m.month, 1).toLocaleString('default', options);
}
// Accepts "31 10 2024", "31/10/2024", "31-10-2024", "31.10.2024".
export function parseDayMonthYear(value) {
    const m = String(value || '').match(/(\d{1,2})\D+(\d{1,2})\D+(\d{4})/);
    return m ? new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10)) : null;
}
export function isPayslip(doc) {
    const t = (doc?.documentType || '').toLowerCase();
    const n = (doc?.name || '').toLowerCase();
    if (isHmrcDoc(doc))
        return false;
    return t === 'payslip' || t === 'payroll' || n.includes('payslip');
}
export function hmrcKind(doc) {
    const t = (doc?.documentType || '').toLowerCase();
    const n = (doc?.name || '').toLowerCase();
    if (t === 'p60' || n.includes('p60'))
        return 'P60';
    if (t === 'p11d' || n.includes('p11d'))
        return 'P11D';
    if (t === 'p45' || n.includes('p45'))
        return 'P45';
    return null;
}
export function isHmrcDoc(doc) {
    return hmrcKind(doc) !== null;
}
// Pay period codes as printed on payslips: "M5" (monthly), "W12" (weekly), "2W6" (fortnightly), "4W3" (four-weekly).
export function parsePayPeriodCode(text) {
    const m = String(text || '').trim().match(/^(2W|4W|W|M)\s*0*(\d{1,2})$/i);
    return m ? { frequency: m[1].toUpperCase(), number: parseInt(m[2], 10) } : null;
}
function employerKey(p) {
    return (p.employer || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}
function byPeriodThenPaid(a, b) {
    return (a.periodEnd?.getTime() ?? 0) - (b.periodEnd?.getTime() ?? 0)
        || (a.datePaid?.getTime() ?? 0) - (b.datePaid?.getTime() ?? 0);
}
// The same payslip uploaded more than once (same employer, period, gross and net): keep the newest upload.
export function dedupePayslips(list) {
    const kept = new Map();
    for (const p of list) {
        const key = [employerKey(p), p.periodEnd?.getTime(), p.grossPay, p.netPay].join('|');
        const existing = kept.get(key);
        if (!existing || p.createdAt > existing.createdAt)
            kept.set(key, p);
    }
    return [...kept.values()];
}
// Months from April of the current tax year (or of the oldest payslip's tax year, if earlier) to the latest month
// that is covered or past its expected payday. Future months, and this month before payday, aren't included.
export function buildMonths(list, today = new Date()) {
    const dated = list.filter(p => p.periodEnd).sort(byPeriodThenPaid);
    const latest = dated[dated.length - 1];
    const payday = latest?.datePaid?.getDate() ?? 28;
    const taxYearStart = today < new Date(today.getFullYear(), 3, 6) ? today.getFullYear() - 1 : today.getFullYear();
    let start = taxYearStart * 12 + 3;
    let end = today.getFullYear() * 12 + today.getMonth() - (today.getDate() >= payday ? 0 : 1);
    if (dated.length) {
        const first = dated[0].periodEnd;
        const last = latest.periodEnd;
        start = Math.min(start, taxYearOfMonth(first.getFullYear(), first.getMonth()) * 12 + 3);
        end = Math.max(end, last.getFullYear() * 12 + last.getMonth());
    }
    const months = [];
    for (let i = start; i <= end; i++) {
        const year = Math.floor(i / 12);
        const month = i % 12;
        const short = monthName({ year, month }, { month: 'short' });
        months.push({
            key: monthKey(year, month),
            year,
            month,
            label: month === 0 ? `${short} '${String(year).slice(2)}` : short,
            payslips: dated.filter(p => p.periodEnd.getFullYear() === year && p.periodEnd.getMonth() === month),
        });
    }
    return months;
}
// Several payslips in one month (bonus run, two employers): this-period amounts are summed; YTD comes from
// each employer's latest payslip, summed across employers.
export function combineMonth(payslips) {
    if (!payslips.length)
        return null;
    const sorted = [...payslips].sort(byPeriodThenPaid);
    const latest = sorted[sorted.length - 1];
    if (sorted.length === 1)
        return latest;
    const latestPerEmployer = new Map();
    for (const p of sorted)
        latestPerEmployer.set(employerKey(p), p);
    const combined = { ...latest };
    for (const f of AMOUNT_FIELDS)
        combined[f] = sorted.reduce((sum, p) => sum + (p[f] || 0), 0);
    for (const f of YTD_FIELDS)
        combined[f] = [...latestPerEmployer.values()].reduce((sum, p) => sum + (p[f] || 0), 0);
    return combined;
}
// Gross minus net minus the deductions we recognise. Anything left is an unrecognised deduction;
// a negative result means the payslip doesn't reconcile.
export function unexplainedDeductions(p) {
    return Math.round((p.grossPay - p.netPay - p.tax - p.ni - p.salarySacrifice) * 100) / 100;
}
export function otherDeductions(p) {
    return Math.max(0, unexplainedDeductions(p));
}
// Four gridlines on a 1/2/3/5 step so the tallest bar never passes the top line.
export function niceAxis(tallest) {
    const rough = (tallest > 0 ? tallest : 1000) / 4;
    const mag = 10 ** Math.floor(Math.log10(rough));
    const step = [1, 2, 3, 5, 10].find(s => s * mag >= rough) * mag;
    return { max: step * 4, interval: step };
}
// How complete this tax year's record is. Monthly payroll counts months up to the last one past payday;
// other frequencies count distinct period numbers against the periods elapsed since the latest payslip.
export function payslipCoverage(list, months, today = new Date()) {
    const dated = list.filter(p => p.periodEnd).sort(byPeriodThenPaid);
    const latest = dated[dated.length - 1];
    if (!latest)
        return null;
    const taxYearStart = today < new Date(today.getFullYear(), 3, 6) ? today.getFullYear() - 1 : today.getFullYear();
    const yearMonths = months.filter(m => taxYearOfMonth(m.year, m.month) === taxYearStart);
    const oldestMissingKey = yearMonths.find(m => !m.payslips.length)?.key ?? null;
    const frequency = latest.payFrequency || 'M';
    if (frequency === 'M') {
        return { taxYearStart, held: yearMonths.filter(m => m.payslips.length).length, expected: yearMonths.length, latest, oldestMissingKey };
    }
    const inYear = dated.filter(p => taxYearOfMonth(p.periodEnd.getFullYear(), p.periodEnd.getMonth()) === taxYearStart);
    const held = new Set(inYear.map(p => p.periodNumber).filter(n => n != null)).size;
    const last = inYear[inYear.length - 1];
    const periodDays = 364 / PERIODS_PER_YEAR[frequency];
    const elapsed = last?.periodNumber && last.datePaid
        ? last.periodNumber + Math.floor((today.getTime() - last.datePaid.getTime()) / (periodDays * DAY))
        : Math.floor((today.getTime() - new Date(taxYearStart, 3, 6).getTime()) / (periodDays * DAY));
    const expected = Math.max(held, Math.min(elapsed, PERIODS_PER_YEAR[frequency]));
    return { taxYearStart, held, expected, latest, oldestMissingKey };
}
const COMPARED_LINES = [
    { key: 'grossPay', label: 'gross pay' },
    { key: 'tax', label: 'tax' },
    { key: 'ni', label: 'National Insurance' },
    { key: 'salarySacrifice', label: 'salary sacrifice' },
    { key: 'other', label: 'other deductions' },
];
function lineValue(p, key) {
    return key === 'other' ? otherDeductions(p) : p[key] || 0;
}
function capitalise(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
// "£266.80 less than July, mainly because tax went up by £266.80." Compares the month at `index` with the previous
// month that has a real payslip (estimates never count). Empty when there's nothing to compare with.
export function monthOnMonth(months, index, format) {
    const current = combineMonth(months[index]?.payslips || []);
    if (!current)
        return '';
    let prevIndex = index - 1;
    while (prevIndex >= 0 && !months[prevIndex].payslips.length)
        prevIndex--;
    if (prevIndex < 0)
        return '';
    const previous = combineMonth(months[prevIndex].payslips);
    const name = monthName(months[prevIndex]);
    const ref = prevIndex === index - 1 ? name : `${name} (your last payslip before this one)`;
    const changes = COMPARED_LINES.map(l => ({ ...l, now: lineValue(current, l.key), before: lineValue(previous, l.key) }))
        .map(c => ({ ...c, change: c.now - c.before }));
    const driver = changes.reduce((big, c) => Math.abs(c.change) > Math.abs(big.change) ? c : big);
    const isNew = (c) => c.before < 0.005 && c.now >= 0.005;
    const isGone = (c) => c.before >= 0.005 && c.now < 0.005;
    const sentences = [];
    const diff = current.netPay - previous.netPay;
    if (Math.abs(diff) < 1) {
        sentences.push(`Same as ${ref}.`);
    }
    else {
        let because = '';
        if (Math.abs(driver.change) >= 1) {
            because = isNew(driver) ? `, mainly because ${driver.label} started this month (${format(driver.now)})`
                : isGone(driver) ? `, mainly because there was no ${driver.label} this month (was ${format(driver.before)})`
                    : `, mainly because ${driver.key === 'grossPay' ? 'your ' : ''}${driver.label} went ${driver.change > 0 ? 'up' : 'down'} by ${format(Math.abs(driver.change))}`;
        }
        sentences.push(`${format(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than ${ref}${because}.`);
    }
    for (const c of changes) {
        if (c === driver && Math.abs(diff) >= 1)
            continue;
        if (isNew(c))
            sentences.push(`${capitalise(c.label)} started this month (${format(c.now)}).`);
        else if (isGone(c))
            sentences.push(`No ${c.label} this month (was ${format(c.before)}).`);
    }
    const codeNow = current.taxCode.trim().toUpperCase();
    const codeBefore = previous.taxCode.trim().toUpperCase();
    if (codeNow && codeBefore && codeNow !== codeBefore)
        sentences.push(`Your tax code changed from ${previous.taxCode.trim()} to ${current.taxCode.trim()}.`);
    return sentences.join(' ');
}
// From each tax year's latest payslip: YTD gross (less any previous-employment figure) minus the gross of
// payslips we hold is what was earned in the missing months. Split per month only when every payslip that year
// has the same gross and the split matches it to within £1; otherwise report the total. Only gaps between two
// payslips in the same tax year get an estimate bar; months before the first one stay "Missing".
export function estimateMissingMonths(months) {
    const estimates = { byMonth: new Map(), byTaxYear: new Map() };
    const years = new Map();
    for (const m of months) {
        const ty = taxYearOfMonth(m.year, m.month);
        years.set(ty, [...(years.get(ty) || []), m]);
    }
    for (const [taxYear, yearMonths] of years) {
        const lastHeldIndex = yearMonths.map(m => m.payslips.length > 0).lastIndexOf(true);
        if (lastHeldIndex < 0)
            continue;
        const upTo = yearMonths.slice(0, lastHeldIndex + 1);
        const missing = upTo.filter(m => !m.payslips.length);
        if (!missing.length)
            continue;
        const held = upTo.filter(m => m.payslips.length).map(m => combineMonth(m.payslips));
        const latest = held[held.length - 1];
        const unaccounted = latest.grossPayYTD - latest.previousEmploymentPay - held.reduce((sum, p) => sum + p.grossPay, 0);
        if (unaccounted < 1)
            continue;
        const monthly = upTo.every(m => m.payslips.every(p => (p.payFrequency || 'M') === 'M'));
        const sameGross = held.every(p => Math.abs(p.grossPay - held[0].grossPay) <= 1);
        if (monthly && sameGross && Math.abs(unaccounted / missing.length - held[0].grossPay) <= 1) {
            const firstHeldIndex = upTo.findIndex(m => m.payslips.length);
            for (const m of upTo.slice(firstHeldIndex + 1).filter(mm => !mm.payslips.length)) {
                estimates.byMonth.set(m.key, { gross: held[0].grossPay, sourceKey: upTo[lastHeldIndex].key });
            }
        }
        else {
            estimates.byTaxYear.set(taxYear, { amount: unaccounted, missing });
        }
    }
    return estimates;
}
// "Apr to Jun" for a run of consecutive months, otherwise "May, Jul and Aug".
export function describeMonths(list) {
    const names = list.map(m => monthName(m, { month: 'short' }));
    const consecutive = list.every((m, i) => i === 0 || (m.year * 12 + m.month) - (list[i - 1].year * 12 + list[i - 1].month) === 1);
    if (names.length === 1)
        return names[0];
    if (consecutive)
        return `${names[0]} to ${names[names.length - 1]}`;
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
// The month to show on return: the saved one if it still has a payslip (e.g. not just deleted), otherwise the
// nearest month that has one (the later month on a tie), otherwise the latest month.
export function nearestMonthWithPayslips(months, key) {
    const held = months.map((m, i) => ({ m, i })).filter(x => x.m.payslips.length);
    if (!held.length)
        return months[months.length - 1]?.key ?? null;
    const from = months.findIndex(m => m.key === key);
    if (from < 0)
        return held[held.length - 1].m.key;
    return held.reduce((best, x) => Math.abs(x.i - from) <= Math.abs(best.i - from) ? x : best).m.key;
}
// Parsing a document into a payslip or HMRC form, shared by the mobile and desktop Payslips pages.
export function toNumber(value) {
    if (value == null)
        return 0;
    return parseFloat(String(value).replace(/[£,]/g, '')) || 0;
}
function findComponent(list, description) {
    if (!Array.isArray(list))
        return { thisPay: 0, ytd: 0 };
    const match = list.find((item) => item && (item.description === description || item.component === description || item.type === description || item.item === description));
    if (!match)
        return { thisPay: 0, ytd: 0 };
    return { thisPay: toNumber(match.thisPay ?? match.amount), ytd: toNumber(match.yearToDate) };
}
// Raw `meta` is the unnormalised extraction output — its shape drifts between
// runs (e.g. `payments` vs `financials`, `type`/`amount` vs `item`/`thisPay`),
// so this only serves as a fallback behind the normalised DataArray.
function parseRawMeta(payslip) {
    let meta = {};
    try {
        meta = JSON.parse(payslip.meta);
    }
    catch (e) { /* tolerate missing/invalid meta */ }
    const summary = meta.financialSummary || meta.financial || meta.financials || meta.earnings || meta.payments || {};
    const components = meta.payComponents || meta.components || {};
    const wages = components.wagesAndEarnings || components.earnings || components.wages;
    const employerList = components.employerContributions || meta.employerContributions;
    const grossAmount = toNumber(summary.grossEarnings ?? summary.gross);
    const taxableAmount = toNumber(summary.taxableEarnings ?? summary.taxable);
    const netAmount = toNumber(summary.netPayment ?? summary.net);
    const salary = findComponent(wages, 'Salary');
    const ni = findComponent(components.deductions, 'National Insurance');
    const tax = findComponent(components.taxes, 'PAYE');
    const salarySacrifice = findComponent(components.preTaxDeductions, 'Pension Salary Sacrifice');
    const employerNi = findComponent(employerList, 'National Insurance');
    const employerPension = findComponent(employerList, 'Pension');
    return {
        grossPay: grossAmount,
        grossPayYTD: salary.ytd,
        salarySacrifice: salarySacrifice.thisPay || (grossAmount - taxableAmount),
        salarySacrificeYTD: salarySacrifice.ytd,
        ni: ni.thisPay,
        niYTD: ni.ytd,
        tax: tax.thisPay,
        taxYTD: tax.ytd,
        netPay: netAmount,
        employerNi: employerNi.thisPay,
        employerNiYTD: employerNi.ytd,
        employerPension: employerPension.thisPay,
        employerPensionYTD: employerPension.ytd,
        taxCode: meta?.employee?.taxCode || meta?.taxDetails?.taxCode || meta?.taxCode || '',
        datePaid: meta?.payPeriod?.datePaid || meta?.payPeriodDetails?.datePaid || meta?.period?.datePaid || meta?.datePaid || '',
        employer: meta?.company || meta?.employerName || '',
        payPeriodCode: [meta?.payPeriod, meta?.payPeriod?.period, meta?.payPeriod?.payPeriod, meta?.payPeriod?.payPeriodCode, meta?.payPeriodDetails?.payPeriod, meta?.period?.payPeriod]
            .find(v => typeof v === 'string') || '',
    };
}
export function normaliseTitle(title) {
    return (title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}
// processedMeta.DataArray is the server's normalised, schema-stable view of a
// payslip (a flat [{Title, Value}] list). It's the same source DocumentPage
// renders from, which is why the document breakdown is correct for every
// variant while the raw-meta parse was not. Build a lookup keyed by normalised
// title so trivial naming drift ("PAYE" vs "PAYE This Pay") still matches.
export function buildFieldMap(payslip) {
    const map = new Map();
    try {
        const arr = JSON.parse(payslip.processedMeta)?.DataArray;
        if (Array.isArray(arr)) {
            for (const item of arr) {
                if (item?.Title != null && item.Value != null) {
                    map.set(normaliseTitle(item.Title), String(item.Value));
                }
            }
        }
    }
    catch (e) { /* tolerate missing/invalid processedMeta */ }
    return map;
}
export function parsePayslipMeta(payslip) {
    const raw = parseRawMeta(payslip);
    const fields = buildFieldMap(payslip);
    // Prefer the normalised DataArray; fall back to the raw-meta parse only when
    // a field is genuinely absent (e.g. an older variant that omits a YTD line).
    const pick = (candidates, fallback) => {
        for (const c of candidates) {
            const v = fields.get(normaliseTitle(c));
            if (v != null && v.trim() !== '')
                return toNumber(v);
        }
        return fallback;
    };
    // Older extractions pack a line and its YTD into one value under a section title,
    // e.g. "Taxes": "PAYE £2,131.26 £16,882.10". Only used when the line has no field of its own.
    const combined = (label) => {
        for (const v of fields.values()) {
            const m = v.match(new RegExp(`^${label}\\s+£?([\\d,]+\\.\\d{2})(?:\\s+£?([\\d,]+\\.\\d{2}))?`, 'i'));
            if (m)
                return { thisPay: toNumber(m[1]), ytd: toNumber(m[2]) };
        }
        return null;
    };
    const combinedTax = combined('PAYE');
    const combinedNi = combined('National Insurance');
    const grossPay = pick(['Gross Earnings', 'Gross Pay', 'Gross', 'Total Gross Pay', 'Total Payments'], raw.grossPay);
    const taxablePay = pick(['Taxable Earnings', 'Taxable Pay'], 0);
    // No salary sacrifice line: pre-tax deductions are the gap between gross and taxable pay.
    const derivedSalarySacrifice = taxablePay > 0 ? Math.max(0, Math.round((grossPay - taxablePay) * 100) / 100) : 0;
    const period = parsePayPeriodCode(['Pay Period', 'Period', 'Pay Period Code', 'Period Type']
        .map(t => fields.get(normaliseTitle(t)))
        .find(v => parsePayPeriodCode(v)) || raw.payPeriodCode);
    return {
        grossPay,
        // Gross YTD first: salary YTD leaves out bonuses. Titles vary between "Year To Date" and "YTD".
        grossPayYTD: pick(['Gross Year To Date', 'Gross YTD', 'Gross Pay Year To Date', 'Gross Pay YTD', 'Gross Pay To Date', 'Salary Year To Date', 'Salary YTD'], raw.grossPayYTD),
        salarySacrifice: pick(['Pension Salary Sacrifice This Pay', 'Pension Salary Sacrifice', 'Salary Sacrifice'], raw.salarySacrifice || derivedSalarySacrifice),
        salarySacrificeYTD: pick(['Pension Salary Sacrifice Year To Date', 'Pension Salary Sacrifice YTD'], raw.salarySacrificeYTD),
        ni: pick(['National Insurance This Pay', 'National Insurance'], raw.ni || combinedNi?.thisPay || 0),
        niYTD: pick(['National Insurance Year To Date', 'National Insurance YTD'], raw.niYTD || combinedNi?.ytd || 0),
        tax: pick(['PAYE This Pay', 'PAYE', 'Tax This Pay', 'Tax', 'Income Tax'], raw.tax || combinedTax?.thisPay || 0),
        taxYTD: pick(['PAYE Year To Date', 'PAYE YTD', 'Tax Year To Date', 'Tax YTD', 'Total Tax To Date'], raw.taxYTD || combinedTax?.ytd || 0),
        netPay: pick(['Net Payment', 'Net Pay', 'Net Take Home Pay'], raw.netPay),
        employerNi: pick(['Employer NI Contribution This Pay', 'Employer NI Contribution', 'Employer National Insurance'], raw.employerNi),
        employerNiYTD: pick(['Employer NI Contribution Year To Date', 'Employer NI Contribution YTD', 'Employer National Insurance Year To Date', 'Employer National Insurance YTD'], raw.employerNiYTD),
        employerPension: pick(['Employer Pension Contribution This Pay', 'Employer Pension Contribution', 'Employer Pension'], raw.employerPension),
        employerPensionYTD: pick(['Employer Pension Contribution Year To Date', 'Employer Pension Contribution YTD', 'Employer Pension Year To Date', 'Employer Pension YTD'], raw.employerPensionYTD),
        taxCode: fields.get(normaliseTitle('Tax Code')) || raw.taxCode || '',
        payFrequency: period?.frequency ?? null,
        periodNumber: period?.number ?? null,
        // A starter's YTD can include pay from a previous employer (P45 figures); estimates leave it out.
        previousEmploymentPay: pick(['Previous Employment Pay', 'Previous Pay', 'P45 Pay', 'Pay From Previous Employment'], 0),
        periodEnd: parseDayMonthYear(getPayslipPeriodEnd(payslip)),
        datePaid: parseDayMonthYear(fields.get(normaliseTitle('Date Paid')) || fields.get(normaliseTitle('Pay Date')) || raw.datePaid),
        employer: fields.get(normaliseTitle('Employer Name')) || fields.get(normaliseTitle('Company')) || raw.employer || payslip.company || '',
        createdAt: payslip.createDateTime || '',
        original: payslip
    };
}
export function getPayslipPeriodEnd(payslip) {
    try {
        const meta = JSON.parse(payslip.meta);
        // Schema variants: payPeriod can be an object {periodEnd|end} or a string
        // code (e.g. "M1"), in which case periodEnd lives at the top level.
        if (meta?.payPeriod && typeof meta.payPeriod === 'object') {
            const end = meta.payPeriod.periodEnd || meta.payPeriod.end;
            if (end)
                return end;
        }
        else if (meta?.periodEnd) {
            return meta.periodEnd;
        }
    }
    catch (e) { /* fall through to normalised DataArray */ }
    const fields = buildFieldMap(payslip);
    return fields.get(normaliseTitle('Pay Period End'))
        || fields.get(normaliseTitle('Period Ending'))
        || fields.get(normaliseTitle('Period End'))
        || monthEnd(fields.get(normaliseTitle('Pay Period')))
        || null;
}
// Some payslips only name the month ("September 2026"): treat the last day of that month as the period end.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
function monthEnd(text) {
    const m = String(text || '').trim().match(/^([a-z]{3})[a-z]*\s+(\d{4})$/i);
    const month = m ? MONTHS.indexOf(m[1].toLowerCase()) : -1;
    if (month < 0)
        return null;
    const year = parseInt(m[2], 10);
    return `${new Date(year, month + 1, 0).getDate()}/${month + 1}/${year}`;
}
export function formatTaxYear(startYear) {
    return `${startYear}/${String(startYear + 1).slice(2)}`;
}
// UK tax year runs 6 April to 5 April.
export function taxYearOf(date) {
    const y = date.getFullYear();
    return formatTaxYear(date < new Date(y, 3, 6) ? y - 1 : y);
}
// `format` turns an amount into display currency (state.formatter, so Hide Figures applies).
export function parseHmrcForm(doc, format) {
    const kind = hmrcKind(doc);
    let meta = {};
    try {
        meta = doc.meta ? JSON.parse(doc.meta) : {};
    }
    catch (e) { /* keep empty meta on parse failure */ }
    // Tax year sources in order: a stated range ("2024 to 2025", "2024-25"), a P45's leaving date, renewalDate, then the file name.
    // Older extractions are flat key/value, so the normalised DataArray is checked too.
    const fields = buildFieldMap(doc);
    const taxYearText = String(meta?.taxYear || fields.get(normaliseTitle('Tax Year')) || '');
    const range = taxYearText.match(/(\d{4})\D+\d{2,4}/);
    const leaving = kind === 'P45'
        ? parseDayMonthYear(meta?.leavingDate || meta?.dateOfLeaving || fields.get(normaliseTitle('Leaving Date')))
        : null;
    // A year in the file name (e.g. "..._P11D_2025") is the year the tax year ends.
    const fileNameYear = String(doc.name || '').match(/(?:^|\D)(20\d{2})(?!\d)/)?.[1];
    const year = range ? formatTaxYear(parseInt(range[1], 10))
        : leaving ? taxYearOf(leaving)
            : doc.renewalDate ? taxYearOf(new Date(doc.renewalDate))
                : fileNameYear ? formatTaxYear(parseInt(fileNameYear, 10) - 1)
                    : taxYearText.match(/\d{4}/)?.[0] || '';
    // One key figure per form. Meta shapes vary by extraction run, so the DataArray titles are the fallback.
    const money = (v) => format(toNumber(v));
    let headline = '';
    if (kind === 'P60') {
        const totals = meta?.financialDetails?.totalForYear || meta?.payAndIncomeTaxDetails?.totalForYear;
        const pay = totals?.pay ?? fields.get(normaliseTitle('Total Pay For Year'));
        const tax = totals?.taxDeducted ?? fields.get(normaliseTitle('Total Tax Deducted For Year'));
        if (pay != null)
            headline = `Pay ${money(pay)}` + (tax != null ? ` · Tax ${money(tax)}` : '');
    }
    else if (kind === 'P45') {
        const pay = fields.get(normaliseTitle('Total Pay to Date'));
        const tax = fields.get(normaliseTitle('Total Tax to Date'));
        if (pay != null)
            headline = `Pay to leaving date ${money(pay)}` + (tax != null ? ` · Tax ${money(tax)}` : '');
    }
    else if (kind === 'P11D') {
        const list = Array.isArray(meta?.benefits) ? meta.benefits : Object.values(meta?.benefits || {});
        const total = list.reduce((sum, b) => sum + toNumber(b?.cashEquivalent), 0);
        if (total > 0)
            headline = `Benefits ${money(total)}`;
    }
    if (!headline)
        headline = doc.documentSubject || '';
    const employer = doc.company || fields.get(normaliseTitle('Employer Name')) || fields.get(normaliseTitle('Employer')) || meta?.employerDetails?.name || '';
    return {
        id: doc.id,
        kind,
        year: String(year || 'Unknown year'),
        company: properCase(doc.company || doc.documentSubject || ''),
        employer: properCase(employer),
        fileName: doc.documentName || doc.name || kind,
        dateString: doc.createDateTimeString || '',
        subject: doc.documentSubject || '',
        headline,
        renewalDate: doc.renewalDate || null,
    };
}
export function properCase(str) {
    if (!str)
        return '';
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
export function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
export const FORM_EXPLAINERS = [
    { kind: 'P60', text: 'Your total pay and tax for the tax year from your employer.', when: "By 31 May each year, if you're employed on 5 April" },
    { kind: 'P45', text: 'Your pay and tax so far when you leave a job. Give it to your next employer.', when: 'When you leave a job' },
    { kind: 'P11D', text: 'Benefits you got through work, like a company car or health insurance, which can affect your tax code.', when: 'By 6 July each year, if you had benefits' },
];
